import { useEffect, useRef } from 'react';

import { displayLanguageAtom } from '@/atoms/demoAtoms';
import { useIsScribeEnableRtlEnabled } from '@/features/flags';
import { optimisticallySetUserLanguage } from '@/hooks/useQueryUserLanguage';
import { mapLocaleAliasToSupportedLocale } from '@/utils/i18n/supportedLocales';
import { useTranslations } from '@/utils/translations';
import { type LanguageCode } from '@heidi/schema/ml-scribe';
import { useSetAtom } from 'jotai';

export interface OnboardingLocaleSyncOptions {
  /**
   * Fired once per language VALUE that flips the locale — not once per flip.
   * Used to persist the language mid-flow (LOC-991); pre-auth callers pass
   * nothing. Once-per-value matters: a failed persist's settled invalidation
   * can revert the locale, and notifying on that re-flip would loop the PATCH.
   */
  onLanguageChanged?: (language: LanguageCode) => void;
}

/**
 * Keeps the app locale (router, i18n, Jotai atom, React Query cache) in sync
 * with a given display language. Reacts to language changes so callers only
 * need to update their own state — this hook handles the side effects.
 *
 * Used during onboarding where TranslationsProvider can't help because the
 * user profile doesn't exist yet.
 */
export function useOnboardingLocaleSync(
  language: LanguageCode | null | undefined,
  options?: OnboardingLocaleSyncOptions
) {
  const { locale, changeLocale } = useTranslations();
  const isRtlLocaleFeatureEnabled = useIsScribeEnableRtlEnabled();
  const setDisplayLanguage = useSetAtom(displayLanguageAtom);
  const changeLocaleRef = useRef(changeLocale);
  changeLocaleRef.current = changeLocale;
  const onLanguageChangedRef = useRef(options?.onLanguageChanged);
  onLanguageChangedRef.current = options?.onLanguageChanged;
  const lastNotifiedLanguageRef = useRef<LanguageCode | null>(null);

  useEffect(() => {
    if (!language) return;
    const mapped = mapLocaleAliasToSupportedLocale(language, {
      isRtlLocaleFeatureEnabled,
    });
    if (mapped && mapped !== locale) {
      optimisticallySetUserLanguage(language);
      changeLocaleRef.current(language);
      setDisplayLanguage(language);
      if (lastNotifiedLanguageRef.current !== language) {
        lastNotifiedLanguageRef.current = language;
        onLanguageChangedRef.current?.(language);
      }
    }
  }, [isRtlLocaleFeatureEnabled, language, locale, setDisplayLanguage]);
}
